import {
  ProfitLossDto,
  ProfitLossInputDto,
  ProfitLossUploadResult,
} from "interfaces/ProfitLoss.interface";
import { buildMyStoreUrl } from "config/constants";

const PROFIT_LOSS_ENDPOINT = buildMyStoreUrl("/api/pnl");
const PROFIT_LOSS_TEMPLATE_ENDPOINT = buildMyStoreUrl("/api/pnl/template");
const PROFIT_LOSS_UPLOAD_ENDPOINT = buildMyStoreUrl("/api/pnl/upload");
const DEFAULT_TEMPLATE_FILE_NAME = "profit-loss-template.xlsx";

const getCookie = (name: string): string | null => {
  try {
    const cookies = document.cookie?.split(";") ?? [];
    for (const cookie of cookies) {
      const [key, ...rest] = cookie.trim().split("=");
      if (key === name) return decodeURIComponent(rest.join("="));
    }
  } catch {
    // ignore cookie parsing errors
  }
  return null;
};

const getStoredValue = (name: string): string | null => {
  try {
    if (typeof sessionStorage !== "undefined") {
      const sessionValue = sessionStorage.getItem(name);
      if (sessionValue) return sessionValue;
    }
    if (typeof localStorage !== "undefined") {
      const localValue = localStorage.getItem(name);
      if (localValue) return localValue;
    }
  } catch {
    // ignore storage access errors
  }
  return getCookie(name);
};

const buildProfitLossUrl = (params: {
  storeNumber: string;
  quarter: string;
  fiscalYear: number;
  sourceSystem?: string;
  employeePin?: string;
}): string => {
  const query = new URLSearchParams();
  query.set("storeNumber", params.storeNumber);
  query.set("quarter", params.quarter);
  query.set("fiscalYear", String(params.fiscalYear));
  if (params.sourceSystem) query.set("sourceSystem", params.sourceSystem);
  if (params.employeePin) query.set("employeePin", params.employeePin);
  return `${PROFIT_LOSS_ENDPOINT}?${query.toString()}`;
};

const getErrorMessage = async (response: Response): Promise<string> => {
  try {
    const data = (await response.json()) as { message?: string | string[] };
    const message = data?.message;
    if (Array.isArray(message)) return message.join(", ");
    if (typeof message === "string" && message) return message;
  } catch {
    // ignore json parsing errors
  }
  return `Request failed with status ${response.status}`;
};

export const getStoredStoreNumber = (): string => getStoredValue("storeNumber") || "";

const parseEmployeePinValidationResult = (payload: unknown): boolean => {
  if (typeof payload === "boolean") return payload;
  if (!payload || typeof payload !== "object") return false;

  const record = payload as Record<string, unknown>;
  if (typeof record["result"] === "boolean") return record["result"];
  if (typeof record["isValid"] === "boolean") return record["isValid"];
  if (typeof record["valid"] === "boolean") return record["valid"];
  return false;
};

export const validateProfitLossEmployeePin = async (params: {
  storeNumber: string;
  employeePin: string;
}): Promise<boolean> => {
  const authToken = getStoredValue("authToken");
  const query = new URLSearchParams({
    storeNumber: params.storeNumber,
    employeePin: params.employeePin,
  });

  const response = await fetch(`${buildMyStoreUrl("/api/pnl/validate-employee-pin")}?${query.toString()}`, {
    method: "GET",
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
  });

  if (!response.ok) throw new Error(await getErrorMessage(response));

  const data = (await response.json()) as unknown;
  return parseEmployeePinValidationResult(data);
};

export const getProfitLoss = async (params: {
  storeNumber: string;
  quarter: string;
  fiscalYear: number;
  sourceSystem?: string;
}): Promise<ProfitLossDto | null> => {
  const authToken = getStoredValue("authToken");
  const response = await fetch(buildProfitLossUrl(params), {
    method: "GET",
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
  });

  if (response.status === 404) return null;
  if (!response.ok) throw new Error(await getErrorMessage(response));

  return (await response.json()) as ProfitLossDto;
};

export const upsertProfitLoss = async (
  params: {
    storeNumber: string;
    quarter: string;
    fiscalYear: number;
    sourceSystem?: string;
    employeePin?: string;
  },
  payload: ProfitLossInputDto
): Promise<ProfitLossDto> => {
  const authToken = getStoredValue("authToken");
  const response = await fetch(buildProfitLossUrl(params), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) throw new Error(await getErrorMessage(response));

  return (await response.json()) as ProfitLossDto;
};

const parseAttachmentFileName = (contentDisposition: string | null): string | null => {
  if (!contentDisposition) return null;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1].trim());
  const quotedMatch = /filename="?([^";]+)"?/i.exec(contentDisposition);
  return quotedMatch?.[1]?.trim() || null;
};

const triggerBlobDownload = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

/** Downloads the blank P&L Excel template and saves it to the user's device. */
export const downloadProfitLossTemplate = async (): Promise<void> => {
  const authToken = getStoredValue("authToken");
  const response = await fetch(PROFIT_LOSS_TEMPLATE_ENDPOINT, {
    method: "GET",
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
  });

  if (!response.ok) throw new Error(await getErrorMessage(response));

  const blob = await response.blob();
  const fileName =
    parseAttachmentFileName(response.headers.get("content-disposition")) || DEFAULT_TEMPLATE_FILE_NAME;
  triggerBlobDownload(blob, fileName);
};

/**
 * Uploads a filled-in P&L template. Each value column in the workbook is one store/quarter record;
 * the API returns a per-record result (partial success). `employeePin` authorizes the upload.
 */
export const uploadProfitLossWorkbook = async (
  file: File,
  employeePin: string
): Promise<ProfitLossUploadResult> => {
  const authToken = getStoredValue("authToken");
  const formData = new FormData();
  formData.append("file", file);
  formData.append("employeePin", employeePin);

  const response = await fetch(PROFIT_LOSS_UPLOAD_ENDPOINT, {
    method: "POST",
    // Do not set Content-Type; the browser adds the multipart boundary automatically.
    headers: {
      ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
    },
    body: formData,
  });

  if (!response.ok) throw new Error(await getErrorMessage(response));

  return (await response.json()) as ProfitLossUploadResult;
};
